import axiosInstance from "../common/axiosInstance";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";

export const NOTE_PATHS = Object.freeze({
  GET_INTERACTION_NOTES: "/notes/data",
  UPSERT_INTERACTION_NOTE: "/notes/upsert",
  DELETE_NOTE: "/notes/delete",
  PREP_NOTES: "/notes/prep-notes",
  GENERATE_PREP_NOTES: "/notes/generate-prep-notes",
});

export interface GetNotesParams {
  interactionId: string;
  isTeamInteraction: boolean;
  sharedWithMe?: boolean;
  sharedByMe?: boolean;
  isForOrg?:boolean;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}

export interface UpsertNoteParams {
  text: string;
  interactionId?: string;
  isPrivate: boolean;
  isUpdate?: boolean;
  isTeamInteraction?: boolean;
  noteId?: string;
}

export interface DeleteNoteParams {
  noteId: string;
}

// API call to fetch notes
const getNotes = async ({
  interactionId,
  isTeamInteraction,
  sharedWithMe,
  sharedByMe,
  isForOrg, 
  impersonatingPorId,
  accessLevel,
}: GetNotesParams): Promise<any> => {
  const response = await axiosInstance.get(NOTE_PATHS.GET_INTERACTION_NOTES, {
    params: {
      interactionId,
      isTeamInteraction,
      ...(sharedWithMe !== undefined ? { sharedWithMe } : {}),
       ...(sharedByMe !== undefined ? { sharedByMe } : {}),
       ...(isForOrg !== undefined? {isForOrg} : {}),
       ...(impersonatingPorId !== undefined? {impersonatingPorId} : {}),
       ...(accessLevel !== undefined? {accessLevel} : {}),
    },
  });
  return response;
};

// Query hook to fetch notes
const useGetInteractionNotes = (
  params: GetNotesParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params); // ensures cache key is unique
  return useQuery({
    queryKey: ["get_notes", stringifiedParams],
    queryFn: () => getNotes(params),
    enabled,
    retry: 0,
  });
};

// Shared upsert API call
const upsertNote = async (data: UpsertNoteParams): Promise<any> => {
  const response = await axiosInstance.post(NOTE_PATHS.UPSERT_INTERACTION_NOTE, data);
  return response;
};

const getPrepNotes = async (interactionId: string): Promise<any> => {
  const response = await axiosInstance.get(
    `${NOTE_PATHS.PREP_NOTES}`,
    { params: { interactionId } }
  );
  return response;
};

const generatePrepNotes = async (interactionId: string): Promise<any> => {
  const response = await axiosInstance.get(
    NOTE_PATHS.GENERATE_PREP_NOTES,
    { params: { interactionId } }
  );
  return response;
};

// Mutation for creating a note
const useCreateNoteMutation = (handlerFunctions?: {
  onMutate?: () => void;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}): UseMutationResult<any, any, UpsertNoteParams> => {
  return useMutation({
    mutationKey: ["create_note"],
    mutationFn: upsertNote,
    ...handlerFunctions,
  });
};

// Mutation for updating a note
const useUpdateNoteMutation = (handlerFunctions?: {
  onMutate?: () => void;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}): UseMutationResult<any, any, UpsertNoteParams> => {
  return useMutation({
    mutationKey: ["update_note"],
    mutationFn: upsertNote,
    ...handlerFunctions,
  });
};

// Delete note API call
const deleteNote = async ({ noteId }: DeleteNoteParams): Promise<any> => {
  const response = await axiosInstance.delete(NOTE_PATHS.DELETE_NOTE, {
    params: { noteId },
  });
  return response;
};

// Mutation for deleting a note
const useDeleteNoteMutation = (handlerFunctions?: {
  onMutate?: () => void;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}): UseMutationResult<any, any, DeleteNoteParams> => {
  return useMutation({
    mutationKey: ["delete_note"],
    mutationFn: deleteNote,
    ...handlerFunctions,
  });
};

// Service type
export interface NotesService {
  readonly useGetInteractionNotes: (
    params: GetNotesParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useCreateNoteMutation: typeof useCreateNoteMutation;
  readonly useUpdateNoteMutation: typeof useUpdateNoteMutation;

  readonly useDeleteNoteMutation: (
    handlerFunctions?: {
      onMutate?: () => void;
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
      onSettled?: () => void;
    }
  ) => UseMutationResult<any, any, DeleteNoteParams>;

  readonly useGetPrepNotes: (
    interactionId: string,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGeneratePrepNotesMutation: (
    handlerFunctions?: {
      onMutate?: () => void;
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
      onSettled?: () => void;
    }
  ) => UseMutationResult<any, any, string>;
  
}

const useGetPrepNotes = (
  interactionId: string,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["prep_notes", interactionId],
    queryFn: () => getPrepNotes( interactionId),
    enabled,
    retry: 0,
  });
};

const useGeneratePrepNotesMutation = (handlerFunctions?: {
  onMutate?: () => void;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}): UseMutationResult<any, any, string> => {
  return useMutation<any, any, string>({
    mutationKey: ["generate_prep_notes"],
    mutationFn: generatePrepNotes,
    ...handlerFunctions,
  });
};

// Exported service
const NotesService: NotesService = Object.freeze({
  useGetInteractionNotes,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetPrepNotes,
  useGeneratePrepNotesMutation,
});

export default NotesService;
